import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Spin, Empty } from 'antd';

const DataChart = ({ data, loading, title }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <Empty description="暂无数据" />;
  }

  // 准备图表数据
  const chartData = data.filter(item => item.time && item.value !== null && item.value !== undefined);
  const xAxisData = chartData.map(item => item.time);
  const seriesData = chartData.map(item => parseFloat(item.value) || 0);

  // 判断时间序列类型和获取当前时间
  const isMonthSeries = xAxisData.length > 0 && /^\d{6}$/.test(xAxisData[0]); // 判断是否为月序列（YYYYMM格式）
  const currentDate = new Date();
  
  // 获取当前时间的字符串表示
  let currentTimeStr;
  if (isMonthSeries) {
    // 月序列：YYYYMM格式
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    currentTimeStr = `${year}${month}`;
  } else {
    // 天序列：YYYY/M/D格式
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    currentTimeStr = `${year}/${month}/${day}`;
  }

  // 找到当前时间点的索引
  const currentTimeIndex = xAxisData.findIndex(timeStr => {
    if (isMonthSeries) {
      return timeStr >= currentTimeStr;
    } else {
      // 对于天序列，需要比较日期
      const dataDate = new Date(timeStr.replace(/\//g, '-'));
      const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      return dataDate >= currentDateOnly;
    }
  });

  // 计算数据的最小值和最大值
  const validValues = seriesData.filter(val => !isNaN(val) && isFinite(val));
  const minValue = validValues.length > 0 ? Math.min(...validValues) : 0;
  const maxValue = validValues.length > 0 ? Math.max(...validValues) : 100;
  
  // 设置Y轴范围为最小值的0.9倍到最大值的1.1倍，并取整数
  const yAxisMin = Math.floor(minValue * 0.9);
  const yAxisMax = Math.ceil(maxValue * 1.1);

  // 准备分段数据
  const beforeCurrentData = [];
  const fromCurrentData = [];
  
  for (let i = 0; i < seriesData.length; i++) {
    if (currentTimeIndex === -1 || i < currentTimeIndex) {
      beforeCurrentData.push(seriesData[i]);
      fromCurrentData.push(null);
    } else if (i === currentTimeIndex) {
      // 当前时间点同时包含在两个系列中，确保线条连接
      beforeCurrentData.push(seriesData[i]);
      fromCurrentData.push(seriesData[i]);
    } else {
      beforeCurrentData.push(null);
      fromCurrentData.push(seriesData[i]);
    }
  }

  const option = {
    title: {
      text: title,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'normal'
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        // 找到有有效值的数据点
        const validPoint = params.find(p => p.value !== null && p.value !== undefined);
        if (!validPoint) return '';
        
        return `
          时间: ${validPoint.axisValue}<br/>
          预测或观测值: ${validPoint.value}
        `;
      }
    },
    legend: {
      data: ['历史数据', '当前及未来数据'],
      top: 30
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '80px',
      containLabel: true
    },
    toolbox: {
      feature: {
        saveAsImage: {
          title: '保存图片'
        },
        dataZoom: {
          title: {
            zoom: '区域缩放',
            back: '区域缩放还原'
          }
        },
        restore: {
          title: '还原'
        }
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData,
      axisLabel: {
        rotate: 45,
        interval: Math.ceil(xAxisData.length / 10) // 控制标签显示数量
      }
    },
    yAxis: {
      type: 'value',
      name: '水位（米）',
      nameTextStyle: {
        fontSize: 12
      },
      min: yAxisMin,
      max: yAxisMax
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100
      },
      {
        start: 0,
        end: 100,
        height: 30,
        bottom: 20
      }
    ],
    series: [
      {
        name: '历史数据',
        type: 'line',
        data: beforeCurrentData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: {
          width: 2,
          color: '#1890ff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(24, 144, 255, 0.3)'
            }, {
              offset: 1, color: 'rgba(24, 144, 255, 0.1)'
            }]
          }
        }
      },
      {
        name: '当前及未来数据',
        type: 'line',
        data: fromCurrentData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: {
          width: 2,
          color: '#ff69b4' // 粉红色
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(255, 105, 180, 0.3)'
            }, {
              offset: 1, color: 'rgba(255, 105, 180, 0.1)'
            }]
          }
        }
      }
    ]
  };

  return (
    <ReactECharts 
      option={option} 
      style={{ height: '350px', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default DataChart;